import React, { useCallback, useContext, useRef } from 'react';
import type { FC } from 'react';

import { NewCommentsContext } from '@confluence/comment-context/entry-points/NewCommentsContext.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSubscriptionsDispatchContext } from '@confluence/graphql/entry-points/SubscriptionsContextProvider.ts';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useIsReactionsEnabled } from '@confluence/comments-hooks/entry-points/useIsReactionsEnabled.ts';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { useViewedComments } from '@confluence/unread-comments/entry-points/useViewedComments.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';

import {
	MissedPubSubCommentEventsHandler,
	CommentEventType,
	type MissedCommentsResult,
} from '../MissedPubSubCommentEventsHandler.ts';

import { setUpPageCommentsPubSub, removePageCommentsPubSub } from './pageCommentsPubSub.ts';
import { reloadAndAddPageComments, type PageCommentsReloadFn } from './pageCommentsOperations.ts';

type PageCommentsPubSubHandlerProps = {
	contentId: string;
	contentType?: string | null;
};

export const PageCommentsPubSubHandler: FC<PageCommentsPubSubHandlerProps> = ({
	contentId,
	contentType,
}) => {
	const isReactionsEnabled = useIsReactionsEnabled(contentId);
	const { addNewComment, removeComment, clearNewComments } = useContext(NewCommentsContext);
	const { addSubscriber, removeSubscriber, setContentType, setContentId } =
		useSubscriptionsDispatchContext() || {};
	const subscriptions = useRef<{ delta: string; subscriberId: string }[]>([]);
	const { userId } = useSessionData();

	const { updateUnreadCommentIDSet } = useUnreadCommentsActions();
	const { getViewedCommentsCacheOnly } = useViewedComments({ pageId: contentId });
	const pageMode = useGetPageMode();

	const { getCurrentCommentsPanelState, updateRemovedCommentIdsMap, reopenParentComment } =
		useCommentsDataActions();

	const { setReplyToCommentId, getCommentsPanelState } = useCommentsPanelActions();

	const clearReplyIfRemoved = useCallback(
		({ commentId }: { commentId: string }) => {
			const { replyToCommentId } = getCommentsPanelState();

			if (replyToCommentId === commentId) {
				setReplyToCommentId();
			}
		},
		[getCommentsPanelState, setReplyToCommentId],
	);

	const { deleteCommentInCache, addCommentInCache } = useAllCommentsData({
		contentId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});

	// The missed-events reload path below is dead: it only runs when MissedPubSubCommentEventsHandler
	// fires onEventFetchSuccess, which no longer happens now that cc_fps_migration_comments and
	// confluence_enable_subscriptions-context-provider are permanently enabled (those gates early-return
	// all of MissedPubSubCommentEventsHandler's effects). This no-op reloader is a placeholder to
	// preserve the (unreachable) reloader signature.
	// TODO: This began being flagged by oxlint as part of a feature-gate cleanup PR; investigate separately.
	// eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve source behavior; see TODO above.
	const activeCommentsRefetch: PageCommentsReloadFn = () => Promise.resolve();

	const subscribeToPageCommentsPubSub = useCallback(() => {
		// Early return if pageMode is Edit - do not setup pubsub for edit pages
		if (pageMode === PageMode.EDIT) {
			return;
		}

		if (subscriptions.current.length > 0) {
			return;
		}

		const queryArgs = {
			isReactionsEnabled,
		};

		subscriptions.current =
			setUpPageCommentsPubSub({
				contentId,
				contentType,
				userId,
				reloader: activeCommentsRefetch,
				addNewComment,
				removeComment,
				queryArgs,
				updateRemovedCommentIdsMap,
				reopenParentComment,
				getCurrentCommentsPanelState,
				addSubscriber,
				setContentType,
				setContentId,
				updateUnreadCommentIDSet,
				getViewedCommentsCacheOnly,
				addCommentInCache,
				deleteCommentInCache,
				clearReplyIfRemoved,
			}) ?? [];
	}, [
		contentId,
		contentType,
		userId,
		// oxlint-disable-next-line react-hooks/exhaustive-deps -- Preserve source behavior; see TODO above.
		activeCommentsRefetch,
		addNewComment,
		removeComment,
		isReactionsEnabled,
		updateRemovedCommentIdsMap,
		reopenParentComment,
		getCurrentCommentsPanelState,
		addSubscriber,
		setContentType,
		setContentId,
		updateUnreadCommentIDSet,
		getViewedCommentsCacheOnly,
		addCommentInCache,
		deleteCommentInCache,
		pageMode,
		clearReplyIfRemoved,
	]);

	const unsubscribeFromPageCommentsPubSub = useCallback(() => {
		// Early return if pageMode is Edit - no pubsub was set up for edit pages
		if (pageMode === PageMode.EDIT) {
			return;
		}

		if (subscriptions.current.length === 0) {
			return;
		}

		const queryArgs = {
			isReactionsEnabled,
		};

		removePageCommentsPubSub({
			contentId,
			contentType,
			userId,
			reloader: activeCommentsRefetch,
			addNewComment,
			removeComment,
			queryArgs,
			updateRemovedCommentIdsMap,
			removeSubscriber,
			subscriptions: subscriptions.current,
			getViewedCommentsCacheOnly,
		});

		subscriptions.current = [];
	}, [
		contentId,
		contentType,
		userId,
		// oxlint-disable-next-line react-hooks/exhaustive-deps -- Preserve source behavior; see TODO above.
		activeCommentsRefetch,
		addNewComment,
		removeComment,
		isReactionsEnabled,
		updateRemovedCommentIdsMap,
		removeSubscriber,
		getViewedCommentsCacheOnly,
		pageMode,
	]);

	const handleEventFetchSuccess = useCallback(
		(data: MissedCommentsResult) => {
			if (data && data.contentId === contentId) {
				if (data.commentEvents.length) {
					const commentData = data.commentEvents.map((event) => {
						return { commentId: event.id, accountId: event.accountId };
					});
					void reloadAndAddPageComments(activeCommentsRefetch, addNewComment, commentData, userId);
				}
			}
		},
		[
			contentId,
			addNewComment,
			// oxlint-disable-next-line react-hooks/exhaustive-deps -- Preserve source behavior; see TODO above.
			activeCommentsRefetch,
			userId,
		],
	);

	if (
		!userId ||
		(pageMode === PageMode.EDIT &&
			expValEquals('confluence_comments_get_document_version_number', 'isEnabled', true))
	) {
		return null;
	} else {
		return (
			<ErrorBoundary attribution={Attribution.COLLABORATION}>
				<MissedPubSubCommentEventsHandler
					contentId={contentId}
					eventType={CommentEventType.FOOTER}
					subscribeToPubSubEvents={subscribeToPageCommentsPubSub}
					unsubscribeFromPubSubEvents={unsubscribeFromPageCommentsPubSub}
					onEventFetchSuccess={handleEventFetchSuccess}
					onDocumentReturn={clearNewComments}
					isAutoReloadPageComments
				/>
			</ErrorBoundary>
		);
	}
};
